import { Button } from '@talentmesh/ui/components/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@talentmesh/ui/components/tooltip';
import { cn } from '@talentmesh/ui/lib/utils';

import { type MRT_RowData, type MRT_TableInstance } from '../../types';
import { type ActionIconProps } from '../../types/mrt-ui-props';
import { parseFromValuesOrFunc } from '../../utils/utils';
import classes from './MRT_ExpandAllButton.module.css';

interface Props<TData extends MRT_RowData> extends ActionIconProps {
    table: MRT_TableInstance<TData>;
}

export const MRT_ExpandAllButton = <TData extends MRT_RowData>({ table, ...rest }: Props<TData>) => {
    const {
        getCanSomeRowsExpand,
        getIsAllRowsExpanded,
        getIsSomeRowsExpanded,
        getState,
        options: {
            icons: { IconChevronsDown },
            localization,
            mantineExpandAllButtonProps,
            renderDetailPanel,
        },
        toggleAllRowsExpanded,
    } = table;
    const { density, isLoading } = getState();

    const actionIconProps = {
        ...parseFromValuesOrFunc(mantineExpandAllButtonProps, {
            table,
        }),
        ...rest,
    };

    const isAllRowsExpanded = getIsAllRowsExpanded();

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Button
                    {...actionIconProps}
                    aria-label={localization.expandAll}
                    className={cn(
                        'mrt-expand-all-button text-muted-foreground h-9 w-9',
                        classes.root,
                        actionIconProps?.className,
                        density,
                    )}
                    disabled={isLoading || (!renderDetailPanel && !getCanSomeRowsExpand())}
                    onClick={() => toggleAllRowsExpanded(!isAllRowsExpanded)}
                    size="icon-2xs"
                    title={undefined}
                    variant="ghost"
                >
                    {actionIconProps?.children ?? (
                        <IconChevronsDown
                            className={cn(
                                'size-4',
                                classes.chevron,
                                isAllRowsExpanded ? classes.up : getIsSomeRowsExpanded() ? classes.right : undefined,
                            )}
                        />
                    )}
                </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
                {(actionIconProps?.title as string | undefined) ??
                    (isAllRowsExpanded ? localization.collapseAll : localization.expandAll)}
            </TooltipContent>
        </Tooltip>
    );
};
